import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { usePagination, useSortBy, useTable } from 'react-table';
import SectionHeader from '../SectionHeader';
import Toggle from 'react-toggle';
import { MdDelete, MdOutlineEdit } from 'react-icons/md';
import { ParseTableLabelDate } from 'app/utils/Parsers';
import { Link, useSearchParams } from 'react-router-dom';
import Checkbox from '../checkbox';
import Button from '../button';
import { hasDatePassed } from 'app/utils/hasDatePassed';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import NewInput from '../NewInput';
import NumberInput from '../UI/NumberInput';
import { TableHeaderConstant } from 'app/constants/table-header-constants';

// Define reusable search and pagination logic inside the component
const PaginationTable = ({
  sectionObj,
  columns: tableColumns,
  data = [],
  fulldata = [],
  fetchData = () => {},
  totalNumber,
  dataPerPage = 10,
  handleDelete = () => {},
  handleEdit = () => {},
  handleViewLeaderboard = () => {},
  handleNew = () => {},
  handleStatusChange = () => {},
  currentPage: initialPage = 1,
  setCurrentPage: setInitalPage = null,
  paginationHandler: externalPaginationHandler,
  customerData,
  isFiltering = false,
  values,
  isdeleteFiltering = false,
  handledDeleteStatusChange = () => {},
  handleComplimentaryAction = () => {},
  handleCheckboxChange = () => {},
  bottomButtons = null,
  handleInput = () => {},
  footerData = null,
  // Presentation only, never authorisation: the server refuses edit, delete and
  // complimentary for a package member voucher. Hides all three row actions together.
  isRowActionsHidden = () => false,
  // Rows for which canExpandRow(row) is true get an expand control; renderSubRow(row)
  // supplies the content of the extra row shown beneath. Both are off by default.
  canExpandRow = () => false,
  renderSubRow = null,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filteredData, setFilteredData] = useState(data);
  const searchFromUrl = searchParams.get('search') || '';
  const [searchValue, setSearchValue] = useState(searchFromUrl);
  const [isSearching, setIsSearching] = useState(false);
  const pageFromUrl = parseInt(searchParams.get('page'), 10);
  const [currentPage, setCurrentPage] = useState(
    !isNaN(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : initialPage
  );
  const pageCount = useMemo(
    () => Math.ceil(totalNumber / dataPerPage),
    [totalNumber, dataPerPage]
  );

  const columns = useMemo(() => tableColumns, [tableColumns]);
  const memoizedData = useMemo(() => filteredData, [filteredData]);

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  const tableInstance = useTable(
    {
      autoResetSortBy: false,
      columns,
      data: memoizedData,
      initialState: { pageIndex: currentPage - 1 },
      manualPagination: true,
      pageCount,
      autoResetPage: false,
    },
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    gotoPage,
    // nextPage,
    // previousPage,
    canNextPage,
    canPreviousPage,
    state: { pageIndex },
  } = tableInstance;

  const hasFooter = footerData != null && Object.keys(footerData).length > 0;

  // Open sub-rows, keyed on the server-supplied rowKey. react-table's row.id is a
  // positional index, which would show the wrong sub-row once the list repaginates.
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const toggleExpanded = useCallback((key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Ensure correct page index is synced with currentPage prop
  useEffect(() => {
    if (pageIndex !== currentPage - 1) {
      gotoPage(currentPage - 1);
    }
  }, [currentPage, pageIndex, gotoPage]);

  // Input change handler memoized
  const inputChangeHandler = useCallback((value) => {
    setSearchValue(value);
    setIsSearching(true);
    setCurrentPage(1);
  }, []);

  // Pagination handler memoized
  const paginationHandler = useCallback((newPage) => {
    setIsSearching(false);
    setCurrentPage(newPage);
  }, []);

  // Effect to handle fetching data when currentPage or searchValue changes
  useEffect(() => {
    if (!isSearching && !isFiltering && !isdeleteFiltering) {
      fetchData(currentPage, searchValue, values);
    }
  }, [currentPage, fetchData, searchValue, values, isSearching, isFiltering, isdeleteFiltering]);

  // Trigger search fetch only when necessary
  useEffect(() => {
    if (isSearching) {
      setCurrentPage(1);
      fetchData(1, searchValue, values);
      setIsSearching(false);
    }
  }, [isSearching, searchValue, fetchData, values]);

  useEffect(() => {
    if (isFiltering) {
      setCurrentPage(1);
      fetchData(1, searchValue, values);
    }
  }, [isFiltering, fetchData, searchValue, values]);

  useEffect(() => {
    if (isdeleteFiltering) {
      fetchData(currentPage, searchValue, values);
    }
  }, [isdeleteFiltering, fetchData, searchValue, values, currentPage]);

  useEffect(() => {
    if (pageCount > 0 && currentPage > pageCount) {
      setCurrentPage(pageCount);
    }
  }, [pageCount, currentPage]);

  useEffect(() => {
    setInitalPage && setInitalPage(currentPage);
  }, [currentPage]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('page', String(currentPage));
        if (searchValue) {
          next.set('search', searchValue);
        } else {
          next.delete('search');
        }
        return next;
      },
      { replace: true }
    );
  }, [currentPage, searchValue, setSearchParams]);

  const handleSearch = useCallback(
    (val) => {
      inputChangeHandler(val);
      if (sectionObj?.searchObj?.searchType === 'server') {
        sectionObj?.searchObj?.handleSearch(val);
      } else {
        const lowerCaseVal = val?.toLowerCase();
        const filtered = fulldata?.filter((item) =>
          sectionObj?.searchObj?.keys?.some((key) => {
            const stringValue = item[key]?.toString().toLowerCase();
            return stringValue?.includes(lowerCaseVal);
          })
        );
        setFilteredData(filtered || data);
      }
    },
    [data, fulldata, sectionObj, inputChangeHandler]
  );
  const handlePreviousPageClick = () => {
    if (canPreviousPage) {
      paginationHandler(currentPage - 1);
    }
  };
  const handleNextPageClick = () => {
    if (canNextPage) {
      paginationHandler(currentPage + 1);
    }
  };

  // Memoized render for pagination numbers
  const renderPageNumbers = useCallback(() => {
    const pageNumbers = [];
    if (pageCount <= 7) {
      for (let i = 1; i <= pageCount; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1, 2);
      if (pageIndex <= 3) {
        pageNumbers.push(3, 4, '...');
      } else if (pageIndex < pageCount - 4) {
        pageNumbers.push('...', pageIndex + 1, pageIndex + 2, '...');
      } else {
        pageNumbers.push('...', pageCount - 3, pageCount - 2);
      }
      pageNumbers.push(pageCount - 1, pageCount);
    }

    return pageNumbers.map((number) => (
      <li
        key={number.toString()}
        className={`page-item ${
          number === '...' ? '' : number === pageIndex + 1 ? 'active' : ''
        }`}
        onClick={() => typeof number === 'number' && paginationHandler(number)}
      >
        <Link className='page-link'>{number}</Link>
      </li>
    ));
  }, [pageIndex, pageCount, paginationHandler]);

  return (
    <>
      <SectionHeader
        sectionObj={sectionObj}
        searchVal={searchValue}
        handleSearch={handleSearch}
        customerData={customerData}
      />

      <div className='table-wrapper'>
        <div className='table'>
          <table {...getTableProps()} className='my-table'>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                  {headerGroup.headers.map((column) =>
                    column.id === 'Checkbox' ? (
                      <th {...column.getHeaderProps()} key={column.id}>
                        <div className='flex justify-content-start align-items-center wd-40 table_header_checkbox'>
                          <Checkbox
                            id='checkbox'
                            name='all'
                            onChange={(e) =>
                              handleCheckboxChange('all', e.target.checked)
                            }
                            checked={filteredData.length > 0 && filteredData.every((item) => item?.checked)}
                          />
                          {column.render('Header')}
                        </div>
                      </th>
                    ) : column.isHover ? (
                      <OverlayTrigger
                        key={column.id}
                        placement='top'
                        delay={{ show: 250, hide: 400 }}
                        overlay={(props) => (
                          <Tooltip id='button-tooltip' {...props}>
                            {column.render('Header')}
                          </Tooltip>
                        )}
                      >
                        <th
                          {...column.getHeaderProps()}
                          key={column.id}
                          className='my-table__header_ellipsis'
                        >
                          {column.render('Header')}
                        </th>
                      </OverlayTrigger>
                    ) : (
                      <th {...column.getHeaderProps()} key={column.id}>
                        {column.render('Header')}
                      </th>
                    )
                  )}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()}>
              {page?.map((row) => {
                prepareRow(row);
                const rowIsExpandable = !!canExpandRow(row.original);
                const rowKey = row.original?.rowKey;
                const rowIsExpanded = rowIsExpandable && expandedKeys.has(rowKey);
                // The expand control sits in the first cell that is not the Checkbox
                // cell, so a row can be both tickable and expandable.
                const expandColumnId = row.cells.find(
                  (c) => c.column.id !== 'Checkbox'
                )?.column.id;
                const { key: rowPropsKey, ...rowProps } = row.getRowProps();
                void rowPropsKey;
                return (
                  <React.Fragment key={row.id}>
                  {/* getRowProps() carries its own `key`; spreading it into the
                      tr makes React 18.3 warn about a key passed via spread on
                      every table render. The Fragment above already holds the
                      key, so drop it from the spread (I-1). */}
                  <tr {...rowProps}>
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} key={cell.column.id}>
                        {rowIsExpandable && cell.column.id === expandColumnId && (
                          <button
                            type='button'
                            className='link cursor-pointer'
                            aria-label={rowIsExpanded ? 'Collapse row' : 'Expand row'}
                            aria-expanded={rowIsExpanded}
                            onClick={() => toggleExpanded(rowKey)}
                          >
                            {rowIsExpanded ? '\u25BE' : '\u25B8'}
                          </button>
                        )}
                        {cell.column.id === 'Actions' ? (
                          isRowActionsHidden(row.original) ? null : (
                          <div style={{ width: 'max-content' }}>
                            <span
                              className='link cursor-pointer'
                              onClick={() => handleEdit(row.original)}
                            >
                              <MdOutlineEdit />
                            </span>
                            <span
                              className='link cursor-pointer'
                              onClick={() => {
                                handleDelete(row.original);
                              }}
                            >
                              <MdDelete /> 
                            </span>
                            {cell.column.complimentaryAction && (
                              <span                              
                                className={`link cursor-pointer ${
                                  hasDatePassed(row.original.expiryDate) || (!row.original.remainingVouchers) || 
                                  (!row.original.status)
                                    ? 'disabled'
                                    : ''
                                }`}
                                onClick={() => handleComplimentaryAction( currentPage,row)}
                              >
                                Complimentary
                              </span>
                            )}
                          </div>
                          )
                        ) : cell.column.id === 'Edit Action' ? (
                          <div style={{ width: 'max-content' }}>
                            <span
                              className='link cursor-pointer'
                              onClick={() => handleEdit(row.original)}
                            >
                              <MdOutlineEdit />
                            </span>
                            {cell.column.viewAction && (
                              <span
                                className='link cursor-pointer'
                                onClick={() => handleViewLeaderboard(row.original)}
                              >
                                View Leaderboard
                              </span>
                            )}
                          </div>
                        ) : cell.column.id === 'Status' ? (
                          <span
                            className={
                              row.original.status
                                ? 'active bdg'
                                : 'inactive bdg'
                            }
                          >
                            {row.original.status === true
                              ? 'Active'
                              : 'Inactive'}
                          </span>
                        ) : cell.column.id === 'Campaign Status' ? (
                          <span
                            className={
                              row.original.status === 'ACTIVE'
                                ? 'active bdg'
                                : row.original.status === 'SCHEDULED'
                                ? 'voilet bdg'
                                : row.original.status === 'DEACTIVATED'
                                ? 'inactive bdg'
                                : 'neutral bdg'
                            }
                          >
                            {row.original.status === 'ACTIVE' && 'Active'}
                            {row.original.status === 'SCHEDULED' && 'Scheduled'}
                            {row.original.status === 'DEACTIVATED' && 'Deactivated'}
                            {row.original.status === 'ENDED' && 'Ended'}
                            {/* DRAFT (Phase 9): an unpublished campaign. It
                                was added to the backend's derived statuses and
                                to the list's status FILTER, but not here — so
                                a draft rendered as a styled pill with no text
                                at all. Neutral grey, matching Ended: both mean
                                "not currently reaching members". */}
                            {row.original.status === 'DRAFT' && 'Draft'}
                          </span>
                        ) : cell.column.id === 'View Action' ? (
                          <div style={{ width: 'max-content' }}>
                            <span
                              className='link cursor-pointer'
                              onClick={() => handleEdit(row.original)}
                            >
                              <MdOutlineEdit />
                            </span>
                            <span
                              className='link cursor-pointer'
                              onClick={() => handleDelete(row.original)}
                            >
                              <MdDelete />
                            </span>
                          </div>
                        ) : cell.column.id === 'Customer Status' ? (
                          <div className='flex justify-content-between'>
                            <span
                              className={
                                row.original.status === 1
                                  ? 'active bdg'
                                  : 'inactive bdg'
                              }
                            >
                              {row.original.status === 1
                                ? 'Unblocked'
                                : 'Blocked'}
                            </span>
                            <Toggle
                              className='mx-3'
                              checked={row?.original?.status === 3}
                              icons={false}
                              onChange={() =>
                                handleStatusChange(row.original, currentPage)
                              }
                            />
                          </div>
                        ) : cell.column.id === 'Published Status' ? (
                          <div className='flex justify-content-between'>
                            <span
                              className={
                                row.original.isPublish
                                  ? 'active bdg'
                                  : 'inactive bdg'
                              }
                            >
                              {row.original.isPublish
                                ? 'Published'
                                : 'Unpublished'}
                            </span>
                            <Toggle
                              className='mx-3'
                              checked={!!row?.original?.isPublish}
                              icons={false}
                              onChange={() =>
                                handleStatusChange(row.original, currentPage)
                              }
                            />
                          </div>
                        ) : cell.column.id === 'Restore' ? (
                          <div className='flex justify-content-between'>
                            <span
                              className={
                                row.original.isDeleted
                                  ? 'active bdg'
                                  : 'inactive bdg'
                              }
                            >
                              {row.original.isDeleted ? 'YES' : 'NO'}
                            </span>
                            <Toggle
                              className='mx-3'
                              disabled={!row.original.isDeleted}
                              checked={!row?.original?.isDeleted}
                              icons={false}
                              onChange={() =>
                                handledDeleteStatusChange(
                                  row.original,
                                  currentPage
                                )
                              }
                            />
                          </div>
                        ) : cell.column.id === 'Store Type' ? (
                          <span>
                            {row.original.type === 'restaurant' ? (
                              <span
                                className='text-primary cursor-pointer'
                                onClick={() => handleNew(row.original)}
                              >
                                {'Menu'}
                              </span>
                            ) : (
                              'Store'
                            )}
                          </span>
                        ) : cell.column.id === 'View Details' ? (
                          <span
                            className='text-primary cursor-pointer'
                            onClick={() => handleNew(row.original)}
                          >
                            View Details
                          </span>
                        ) : cell.column.id === 'Collect Voucher' ? (
                          row.original.status === 1 ? (
                            <span
                              className='text-primary cursor-pointer'
                              onClick={() => handleNew(row.original)}
                            >
                              Collect Voucher
                            </span>
                          ) : (
                            <span
                              className='text-secondary'
                              // onClick={() => handleNew(row.original)}
                            >
                              Collect Voucher
                            </span>
                          )
                        ) : cell.column.id === 'Privilege Date' ? (
                          `${ParseTableLabelDate(
                            row.original.startDate
                          )} - ${ParseTableLabelDate(row.original.expiryDate)}`
                        ) : cell.column.id === 'Collect Privilege' ? (
                          row.original?.status === 'Collect' ? (
                            <span
                              className='voilet bdg cursor-pointer'
                              onClick={() => handleNew(row.original)}
                            >
                              Collect Privilege
                            </span>
                          ) : row.original.status === 'collected' ? (
                            <span className='active bdg'>Collected</span>
                          ) : row.original.status === 'history' ||
                            row.original.status === 'locked' ? (
                            // Read-only history (Account deleted) or not-yet-
                            // Influencer (Account restored): show the action but
                            // disabled, not clickable.
                            <span
                              className='voilet bdg'
                              style={{ opacity: 0.45, cursor: 'not-allowed' }}
                            >
                              Collect Privilege
                            </span>
                          ) : (
                            '-'
                          )
                        ) : cell.column.id === 'Bill Status' ? (
                          <span
                            className={
                              row.original?.billStatus === 1
                                ? 'voilet bdg'
                                : row.original?.billStatus === 2
                                ? 'active bdg'
                                : row.original?.billStatus === 3
                                ? 'inactive bdg'
                                : row.original?.billStatus === 4
                                ? 'inactive bdg'
                                : ''
                            }
                          >
                            {row.original?.billStatus === 1 && 'Under Review'}
                            {row.original?.billStatus === 2 && 'Approved'}
                            {row.original?.billStatus === 3 && 'Rejected'}
                            {row.original?.billStatus === 4 && 'Pending'}
                          </span>
                        ) : cell.column.id === 'Voucher Status' ? (
                          <span
                            className={
                              row.original.status === 1
                                ? 'voilet bdg'
                                : row.original.status === 2
                                ? 'inactive bdg'
                                : row.original.status === 3
                                ? 'active bdg'
                                : ''
                            }
                          >
                            {row.original.status === 1 && 'Reserved'}
                            {row.original.status === 2 && 'Cancelled'}
                            {row.original.status === 3 && 'Collected'}
                          </span>
                        ) : cell.column.id === 'Reserve Voucher' ? (
                          row.original.status === 2 ? (
                            <span
                              className='voilet bdg cursor-pointer'
                              onClick={() =>
                                handleEdit(currentPage,row.original)
                              }
                            >
                              Reserve
                            </span>
                          ) : (
                            <span className='text-secondary'>Reserve</span>
                          )
                        ) : cell.column.id === 'Cancel Voucher' ? (
                          row.original.status === 1 ? (
                            <span
                              className='voilet bdg cursor-pointer'
                              onClick={() =>
                                handleDelete(currentPage, row.original)
                              }
                            >
                              Cancel
                            </span>
                          ) : (
                            <span className='text-secondary'>Cancel</span>
                          )
                        ) : cell.column.id === 'Checkbox' ? (
                          <div className='link cursor-pointer flex justify-content-start align-items-center wd-40 table_checkbox'>
                            <Checkbox
                              id='checkbox'
                              name={row.original.name}
                              onChange={(e) =>
                                handleCheckboxChange(
                                  row.original,
                                  e.target.checked
                                )
                              }
                              checked={row.original.checked}
                            />
                          </div>
                        ) : cell.column.type === 'InputAction' ? (
                          <div
                            style={{ width: 'max-content' }}
                            className='cell_input_wrapper'
                          >
                            {cell.column.inputType === 'number' ? (
                              <NumberInput
                                value={
                                  cell.column.id ===
                                  TableHeaderConstant.voucherQtyReceivedByBrandKey
                                    ? row.original.voucher_qty
                                    : cell.column.id ===
                                      TableHeaderConstant.ConsumptionKey
                                    ? row.original.consumption
                                    : ''
                                }
                                onChange={(e) =>
                                  handleInput(
                                    row.original,
                                    cell.column.id,
                                    e.target.value
                                  )
                                }
                              />
                            ) : (
                              <NewInput
                                onChange={(e) =>
                                  handleInput(
                                    row.original,
                                    cell.column.id,
                                    e.target.value
                                  )
                                }
                              />
                            )}
                          </div>
                        ) : (
                          cell.render('Cell')
                        )}
                      </td>
                    ))}
                  </tr>
                  {renderSubRow && rowIsExpanded && (
                    <tr>
                      <td colSpan='1000'>{renderSubRow(row.original)}</td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
              {page.length === 0 && (
                <tr>
                  <td colSpan='1000'>
                    <div className='bottom-pagination'>
                      <span className='content-container__pagination'>
                        No Data Found
                      </span>
                    </div>
                  </td>
                </tr>
              )}
              {bottomButtons && filteredData?.length > 0 && (
                <tr>
                  <td colSpan='1000'>
                    <div className='flex justify-content-start gap-3 align-items-center'>
                      {bottomButtons?.map((btn) => (
                        <div key={btn?.id} className='wd-25'>
                          <Button
                            className={btn?.customClassName}
                            onClick={btn?.handleClick}
                            isDisabled={btn?.disabled}
                          >
                            {btn?.title}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
              {hasFooter &&
                headerGroups.map((headerGroup) => (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                    key={`footer-${headerGroup.id}`}
                  >
                    {headerGroup.headers.map((column) => (
                      <td key={column.id} style={{ fontWeight: 600, borderTop: '2px solid #333' }}>
                        {footerData[column.id] != null ? footerData[column.id] : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              <tr>
                <td colSpan='1000'>
                  <div className='bottom-pagination'>
                    <span className='content-container__pagination__left'>
                      {totalNumber > 0 ? (
                        <>
                          {(() => {
                            // ✅ reset currentPage to 1 if it’s beyond total pages
                            const totalPages = Math.ceil(totalNumber / dataPerPage);
                            const safePage = Math.min(currentPage, totalPages);

                            const startIndex = (safePage - 1) * dataPerPage + 1;
                            const endIndex = Math.min(safePage * dataPerPage, totalNumber);

                            return (
                              <>
                                Showing {startIndex} - {endIndex} of {totalNumber}{' '}
                                {totalNumber === 1 ? 'result' : 'results'}
                              </>
                            );
                          })()}
                        </>
                      ) : (
                        'No results'
                      )}
                    </span>
                    {pageCount > 1 && (
                      <span className='content-container__pagination'>
                        <ul className='pagination'>
                          <li
                            className={`page-item ${
                              !canPreviousPage ? 'inactive' : 'cursor-pointer'
                            }`}
                            onClick={handlePreviousPageClick}
                          >
                            <Link className='page-link'>&lt;</Link>
                          </li>
                          {renderPageNumbers()}
                          <li
                            className={`page-item ${
                              !canNextPage ? 'inactive' : 'cursor-pointer'
                            }`}
                            onClick={handleNextPageClick}
                          >
                            <Link className='page-link'>&gt;</Link>
                          </li>
                        </ul>
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default PaginationTable;
